import { getIn } from 'formik';

import AnomalousPaymentBanner from '~/components/exposures/PaymentRequestContainer/MakePaymentRequestDialog/AnomalousPaymentBanner';
import toCents from '~/Utils/toCents';

import { COUNTRY_TO_STATE_MAP } from '../../../Types';
import { isMgmClaim, isQoverClaim, reportErrorInProductionOrThrow } from '../../../Utils';
import { isLimitExists } from '../ExposureUtils';

import { applyNetToReserveAmount, getNettedSubReserveAmounts } from './netPaidReserveUtils';

export const attachedDocumentsMaxHeight = '200px';

const NZ_BANK_ACCOUNT_WITH_DASHES_REGEX = /^\d{2}-\d{4}-\d{7}-\d{2,3}$/;
const NZ_BANK_ACCOUNT_DIGITS_ONLY_REGEX = /^\d{15,16}$/;

// `requireExact16` is compared strictly to `true`: passed bare to a Yup `.test()`, the second arg
// is the truthy test context, which must not enable the exact-16 rule.
export const isValidNzBankAccount = (value, requireExact16 = false) => {
  if (!value) {
    return true;
  }
  if (!NZ_BANK_ACCOUNT_WITH_DASHES_REGEX.test(value) && !NZ_BANK_ACCOUNT_DIGITS_ONLY_REGEX.test(value)) {
    return false;
  }
  return requireExact16 === true ? value.replace(/-/g, '').length === 16 : true;
};

// Advisory gate behind the payment dialog's "TFN not verified" AlertBanner. The payee contact is
// missing required TFN verification when verification is enabled, the exposure's coverage is one of
// the configured mandatory-TFN coverages, the payee is an AU contact (TFN is AU-specific), and that
// contact's TFN is not verified. Pure so it can be unit-tested without mounting the dialog.
export const isContactMissingTfnVerification = ({
  isVerificationRequired,
  mandatoryCoverages,
  coverageType,
  payee,
}) => {
  const isTfnRequiredForCoverage =
    !!isVerificationRequired && !!coverageType && (mandatoryCoverages || []).includes(coverageType);
  const isPayeeTfnRelevant = payee?.country === 'AU';
  return isTfnRequiredForCoverage && isPayeeTfnRelevant && payee?.is_au_tfn_verified !== true;
};

// Client-side mirror of the backend trusted-payee approval bypass
// (`is_trusted_payee_auto_approvable` in payables_with_reserve_manager). The contact read surface
// already carries the derived arrangement state — `trusted_payee_status` is 'active' only for a
// non-revoked, non-expired arrangement in an org with the flag on — so the dialog can resolve both
// the bypass and the over-limit warning without a round-trip.
const ACTIVE_TRUSTED_PAYEE_STATUS = 'active';

// The bypass is single-payee only; a split payment always takes the normal approval flow.
const getSingleActiveTrustedPayee = (payeeContacts) => {
  if (!payeeContacts || payeeContacts.length !== 1) {
    return null;
  }
  const [payeeContact] = payeeContacts;
  return payeeContact?.trusted_payee_status === ACTIVE_TRUSTED_PAYEE_STATUS ? payeeContact : null;
};

// A null/absent per-payment limit means no cap, so an active arrangement auto-approves at any amount.
// Both sides are rounded to money precision before comparing — the backend quantizes to cents, and a
// currency-conversion epsilon (91.78999999999999 vs a clean 91.79 limit) would otherwise flip the
// gate and make the dialog disagree with the routing it is describing.
const isAboveTrustedPayeeLimit = (trustedPayeeContact, paymentAmount) => {
  const perPaymentLimit = trustedPayeeContact?.trusted_payee_per_payment_limit;
  return isLimitExists(perPaymentLimit) && toCents(paymentAmount) > toCents(perPaymentLimit);
};

// Whether this payment skips the routine `require_approval_for_every_payment` routing and is paid
// straight away. `isTrustedPayeeEnabled` already folds in the feature flag and both org flags.
export const isTrustedPayeeAutoApprovable = ({ isTrustedPayeeEnabled, payeeContacts, paymentAmount }) => {
  if (!isTrustedPayeeEnabled) {
    return false;
  }
  const trustedPayeeContact = getSingleActiveTrustedPayee(payeeContacts);
  return trustedPayeeContact !== null && !isAboveTrustedPayeeLimit(trustedPayeeContact, paymentAmount);
};

// Informational warning for an over-limit payment to a trusted payee: the bypass above would have
// fired but for the amount, so the adjuster is told why this one still needs approval.
export const getTrustedPayeeIssuesAndSeverities = ({
  isTrustedPayeeEnabled,
  payeeContacts,
  paymentAmount,
  currencyFormatter,
}) => {
  if (!isTrustedPayeeEnabled) {
    return [];
  }
  const trustedPayeeContact = getSingleActiveTrustedPayee(payeeContacts);
  if (trustedPayeeContact === null || !isAboveTrustedPayeeLimit(trustedPayeeContact, paymentAmount)) {
    return [];
  }
  return [
    {
      issue: `Payment exceeds the trusted payee limit (${currencyFormatter.format(
        trustedPayeeContact.trusted_payee_per_payment_limit
      )}) and will require approval.`,
      severity: AnomalousPaymentBanner.IssueSeverity.WARNING,
    },
  ];
};

// Whether a record-only entry skips the routine approval. Mirrors the backend's
// `record_only_bypass`: the entry disburses no funds, so an organization that opted in does not put
// a second person on it. The organization toggle is not ANDed with
// `require_approval_for_every_payment` here for the same reason it is not on the backend -- the one
// consumer below already does.
export const isRecordOnlyAutoApprovable = ({ isSkipApprovalForRecordOnlyPayments, isRecordOnly }) =>
  Boolean(isSkipApprovalForRecordOnlyPayments && isRecordOnly);

// Client-side mirror of the backend's `require_additional_approval` disjunction in
// `ManagedPaymentRequest`, and the sole input to the submit button's wording. Both bypasses narrow
// only the routine `require_approval_for_every_payment` term, so they sit inside it: an
// over-authority or method-specific approval still routes the payment to a supervisor.
export const isPaymentRequiringApproval = ({
  isExceedsUserAuthority,
  isRequireApprovalForEveryPayment,
  isTrustedPayeeBypassed,
  isRecordOnlyBypassed,
  isMethodSpecificApproveRequired,
}) =>
  Boolean(
    isExceedsUserAuthority ||
      (isRequireApprovalForEveryPayment && !isTrustedPayeeBypassed && !isRecordOnlyBypassed) ||
      isMethodSpecificApproveRequired
  );

export function getPayeeContacts(values, paymentRequestStoreData, claim, options = {}) {
  const { shouldIncludeOnlyChosen = true, ignoreNonPrimaryPayeeCandadiages = false } = options;
  let chosenPayeeContacts = [];

  if (!paymentRequestStoreData) {
    return chosenPayeeContacts;
  }

  paymentRequestStoreData.forEach((paymentRequestStoreDataEntry) => {
    if (ignoreNonPrimaryPayeeCandadiages && paymentRequestStoreDataEntry.can_be_primary === false) {
      return;
    }
    switch (paymentRequestStoreDataEntry.type) {
      case 'optional_known_contact':
        if (values[paymentRequestStoreDataEntry.field_name] || !shouldIncludeOnlyChosen) {
          const contact =
            paymentRequestStoreDataEntry.contact ||
            claim.contacts.find((contact) => paymentRequestStoreDataEntry.contact_id === contact.id);
          if (contact) {
            chosenPayeeContacts.push(contact);
          } else {
            reportErrorInProductionOrThrow(
              `getPayeeContacts: contact_id ${paymentRequestStoreDataEntry.contact_id} not found in claim.contacts`
            );
          }
        }
        break;
      case 'contact_search_single': {
        // The store entry can outlive its Formik value — a resetForm restores initial values that
        // carry no store-seeded payee fields while the store is still populated. Nothing selected
        // is a normal state, so fall back to an empty entry rather than dereferencing undefined
        // mid-render (the sibling contact_search branch already does this with `|| []`).
        const contactEntry = values[paymentRequestStoreDataEntry.field_name] ?? {};
        if (contactEntry.contact) {
          chosenPayeeContacts.push(contactEntry.contact);
        } else if (contactEntry.contact_id) {
          const contact =
            contactEntry.contact || claim.contacts.find((contact) => contactEntry.contact_id === contact.id);
          if (contact) {
            chosenPayeeContacts.push(contact);
          } else {
            reportErrorInProductionOrThrow(
              `getPayeeContacts: contact_id ${contactEntry.contact_id} not found in claim.contacts`
            );
          }
        }
        break;
      }
      case 'contact_search': {
        const contactEntriesList = values[paymentRequestStoreDataEntry.field_name] || [];
        contactEntriesList.forEach((contactEntry) => {
          if (contactEntry.contact) {
            chosenPayeeContacts.push(contactEntry.contact);
          } else if (contactEntry.contact_id) {
            const contact =
              contactEntry.contact || claim.contacts.find((contact) => contactEntry.contact_id === contact.id);
            if (contact) {
              chosenPayeeContacts.push(contact);
            } else {
              reportErrorInProductionOrThrow(
                `getPayeeContacts: contact_id ${contactEntry.contact_id} not found in claim.contacts`
              );
            }
          }
        });
        break;
      }
      default:
        throw Error(`Unknown paymentRequestStoreDataEntry type: ${paymentRequestStoreDataEntry.type}`);
    }
  });

  return chosenPayeeContacts;
}

export const PAY_TO_LENGTH_VALIDATION_FIELDS = [
  { fieldKey: 'full_name', description: 'full name', maxCharsKey: 'full_name_max_length' },
  { fieldKey: 'city', description: 'city', maxCharsKey: 'city_max_length' },
  { fieldKey: 'street_address1', description: 'street address 1', maxCharsKey: 'address1_max_length' },
  { fieldKey: 'street_address2', description: 'street address 2', maxCharsKey: 'address2_max_length' },
  {
    fieldKey: 'address_line1',
    description: 'address line 1',
    maxCharsKey: 'address_line1_max_length',
    joinedFieldsList: ['street_address1', 'street_address2'],
  },
  {
    fieldKey: 'address_line2',
    description: 'address line 2',
    maxCharsKey: 'address_line2_max_length',
    joinedFieldsList: ['city', 'county', 'state', 'zipcode'],
  },
  { fieldKey: 'pay_to_line', description: 'pay to line', maxCharsKey: 'pay_to_line_max_length' },
];

export const isStateCountry = (country) => country in COUNTRY_TO_STATE_MAP;

export const getCharsLimitValidationMessage = (maxLength) => `Max ${maxLength} characters`;

const getPendingSubReserveAmount = (payableWithReserve, type_key) => {
  return payableWithReserve?.pending_sub_reserve_amounts?.find((item) => item.type_key === type_key)?.amount || 0;
};

// The pending term the reserve will actually move by: approval takes each pending request's net amount
// out of its sub reserve when reserves are reduced by net paid, so the netted sibling of
// `pending_sub_reserve_amounts` is what the gate prices. `net_pending_amounts` is served only to an
// organization with that toggle on, which is the only one that reads it; falling back to the gross
// list when it is absent rather than reading it as zero keeps a payload from before the field existed
// from silently dropping the pending term. An empty *served* list is a real answer -- there are no
// pending payments -- and yields 0 through the lookup.
const getPendingSubReserveAmountForGate = (payableWithReserve, type_key, shouldReduceReservesByNetPaid) => {
  const nettedPendingSubReserveAmounts = payableWithReserve?.net_pending_amounts?.sub_reserve_amounts;
  if (!shouldReduceReservesByNetPaid || !nettedPendingSubReserveAmounts) {
    return getPendingSubReserveAmount(payableWithReserve, type_key);
  }
  return nettedPendingSubReserveAmounts.find((item) => item.type_key === type_key)?.amount || 0;
};

function getAdjustReserveDetails({
  isSubReservesConfigEnabled,
  isMultipleSubReservePaymentsEnabled,
  payableWithReserve,
  paymentAmount,
  amountWithoutDeductible,
  subReserveAmounts = null,
  subReserveKeyForAdjustReserveDeductible = null,
  currentPaymentRequestAmount = 0,
  isApprovalDialogOtherwiseMakePaymentRequest,
  vatAmountToNetOut = 0,
  nettedPendingSum = null,
  shouldReduceReservesByNetPaid = false,
}) {
  const deductibleAmount = toCents(paymentAmount - amountWithoutDeductible);
  const deductibleConsidered = deductibleAmount > 0;
  if (isSubReservesConfigEnabled) {
    const subReserveKeysAboveReserve = [];
    const nettedSubReserveAmounts = getNettedSubReserveAmounts(subReserveAmounts, vatAmountToNetOut);
    subReserveAmounts?.forEach(({ type_key, amount }, lineIndex) => {
      if (!type_key || typeof amount !== 'number') {
        return;
      }
      let consideredAmount = isApprovalDialogOtherwiseMakePaymentRequest ? 0 : nettedSubReserveAmounts[lineIndex];
      let subReserveDeductibleAmount = 0;
      if (subReserveKeyForAdjustReserveDeductible === type_key || !isMultipleSubReservePaymentsEnabled) {
        subReserveDeductibleAmount = deductibleAmount;
        consideredAmount = toCents(consideredAmount + deductibleAmount);
      }

      const pendingSubReserveAmount = getPendingSubReserveAmountForGate(
        payableWithReserve,
        type_key,
        shouldReduceReservesByNetPaid
      );

      // Compare in cents (toCents) so float residue and half-cent reserves can't split this gate from the backend's.
      if (
        toCents(consideredAmount + pendingSubReserveAmount) > toCents(payableWithReserve?.sub_reserves?.[type_key] || 0)
      ) {
        subReserveKeysAboveReserve.push({
          type_key,
          subReserveDeductibleAmount,
          subReservePaymentConsideredAmount: consideredAmount,
        });
      }
    });
    return {
      shouldAdjust: subReserveKeysAboveReserve.length > 0,
      subReserveKeysAboveReserve,
      deductibleConsidered,
    };
  }
  // Netted like the backend gate this previews: with reserves reduced by net paid, approval takes the
  // net amount out of the reserve, so measuring the gross one flags — and, when payments above reserve
  // are prohibited, disables — payments the backend accepts. The deductible is not VAT-bearing, so it
  // is netted out of `amountWithoutDeductible`'s share only.
  let consideredAmountForAdjustment =
    applyNetToReserveAmount(paymentAmount - deductibleAmount, vatAmountToNetOut) +
    deductibleAmount +
    (nettedPendingSum !== null ? nettedPendingSum : payableWithReserve?.pending_sum);
  if (isApprovalDialogOtherwiseMakePaymentRequest) {
    // The request being approved is itself one of the pending payments, so it is removed from the
    // pending term to avoid counting it twice — netted, because that is how the pending term counted
    // it. Removing the gross amount from a netted sum takes this request's VAT out a second time and
    // leaves the dialog silent on an approval the reserve has no room for.
    consideredAmountForAdjustment =
      consideredAmountForAdjustment - applyNetToReserveAmount(currentPaymentRequestAmount, vatAmountToNetOut);
  }
  return {
    shouldAdjust: toCents(consideredAmountForAdjustment) > toCents(payableWithReserve.reserve),
    subReserveKeysAboveReserve: [],
    deductibleConsidered,
  };
}

export const getPaymentsAboveReserveIssuesAndSeverities = ({
  isSubReservesConfigEnabled,
  isMultipleSubReservePaymentsEnabled,
  payableWithReserve,
  paymentAmount,
  amountWithoutDeductible,
  subReserveAmounts = null,
  subReserveKeyForAdjustReserveDeductible = null,
  shouldDisablePaymentExceedingReserve,
  subReservesConfigMap,
  currencyFormatter,
  currentPaymentRequestAmount = 0,
  isApprovalDialogOtherwiseMakePaymentRequest,
  vatAmountToNetOut = 0,
  shouldReduceReservesByNetPaid = false,
}) => {
  const issuesAndSeverities = [];
  // Pending payments move the reserve by their own net amount too, so the pending term follows the
  // toggle and not this request's VAT — the backend nets it whenever the toggle is on, whether or not
  // the request being priced carries any VAT itself.
  //
  // `net_pending_amounts.sum` is the manager's own per-request netted total — the figure the gate that
  // actually refuses the payment prices. It is not `net_pending_sum`, which subtracts `vat_pending_sum`
  // (raw `vat_amount`, in each request's own currency) from `pending_sum` (policy currency) and clamps
  // in aggregate: a pending 1000 EUR at 0.9 carrying 100 EUR of VAT reads 1011.11 there against the
  // 1000.00 the gate prices, which is enough to disable submitting a payment the backend accepts.
  // Falls back to that approximation only when the field is absent, for the reason given above
  // `getPendingSubReserveAmountForGate`.
  const nettedPendingSum = shouldReduceReservesByNetPaid
    ? (payableWithReserve?.net_pending_amounts?.sum ?? payableWithReserve?.net_pending_sum ?? null)
    : null;
  const { shouldAdjust, subReserveKeysAboveReserve, deductibleConsidered } = getAdjustReserveDetails({
    isSubReservesConfigEnabled,
    isMultipleSubReservePaymentsEnabled,
    payableWithReserve,
    paymentAmount,
    amountWithoutDeductible,
    subReserveAmounts,
    subReserveKeyForAdjustReserveDeductible,
    currentPaymentRequestAmount,
    isApprovalDialogOtherwiseMakePaymentRequest,
    vatAmountToNetOut,
    nettedPendingSum,
    shouldReduceReservesByNetPaid,
  });
  if (shouldAdjust) {
    const paymentAboveReserveSeverity = shouldDisablePaymentExceedingReserve
      ? AnomalousPaymentBanner.IssueSeverity.ERROR
      : AnomalousPaymentBanner.IssueSeverity.WARNING;

    if (subReserveKeysAboveReserve.length > 0) {
      subReserveKeysAboveReserve.forEach(({ type_key, subReserveDeductibleAmount }) => {
        // The same figure the gate above added, so the banner names the amount that made it fire.
        const pendingAmount = getPendingSubReserveAmountForGate(
          payableWithReserve,
          type_key,
          shouldReduceReservesByNetPaid
        );
        const reserveAmount = payableWithReserve?.sub_reserves?.[type_key] || 0;
        const subReserveDesc = subReservesConfigMap.get(type_key)?.desc;

        issuesAndSeverities.push({
          issue: `Payment ${subReserveDeductibleAmount > 0 ? 'including deductible ' : ''} 
            ${pendingAmount ? ` and pending payments (${currencyFormatter.format(pendingAmount)})` : ''}
             exceeds reserves. Current reserve for Sub-reserve "${subReserveDesc}" is ${currencyFormatter.format(reserveAmount)}.`,
          severity: paymentAboveReserveSeverity,
        });
      });
    } else {
      // As in the sub-reserve branch: the pending figure the gate priced, not its gross sibling.
      const pendingAmount = nettedPendingSum !== null ? nettedPendingSum : payableWithReserve?.pending_sum;
      const hasPendingPayments = pendingAmount > 0;

      issuesAndSeverities.push({
        issue: `${
          deductibleConsidered
            ? 'Payment including deductible (' + currencyFormatter.format(paymentAmount) + ')'
            : 'Payment'
        } ${hasPendingPayments ? ` and pending payments (${currencyFormatter.format(pendingAmount)})` : ``}
        exceeds reserves (Current reserve: ${currencyFormatter.format(payableWithReserve.reserve)}).`,

        severity: paymentAboveReserveSeverity,
      });
    }
  }

  return issuesAndSeverities;
};

const keepReferenceAsIs = (reference) => reference;

// A provider that restricts what its reference field accepts overrides this; every other provider
// gets the suggested value untouched (a hyphen is valid on Commerce Bank, One Inc and the legacy
// rails). Resolved from the whole method configuration rather than a provider string, so a rail that
// needs a per-method rule later is a branch here and not a new argument at the call sites.
const SUGGESTED_REFERENCE_SANITIZERS_BY_PROVIDER = {
  // Vitesse types every reference as `Extended Alpha Numeric` (the per-character `isalnum()` admits
  // any unicode letter, so accented and non-Latin references survive while punctuation, tabs and
  // newlines do not) and its UK rail narrows that to alphanumeric-only with a whole-string
  // `reference.isalnum()` in `vitesse_payment_provider_handler`, so the stricter of the two is what
  // we suggest. Two methods it over-reaches on, both suggestion-only and both harmless: an
  // `iban_bank_transfer` misconfigured onto Vitesse, which `get_field_rule_issues` skips and so
  // applies no reference rule to; and a cheque, where the Reference field is not rendered at all and
  // Vitesse receives the memo instead. `invoice_number` is neither required nor Yup-validated on the
  // use-invoice path, so an absent value reaches here and passes through rather than being
  // dereferenced.
  vitesse: (reference) => (reference ? reference.replace(/[^\p{L}\p{N}]/gu, '') : reference),
};

export const getSuggestedReferenceSanitizer = (paymentMethodConfiguration) =>
  SUGGESTED_REFERENCE_SANITIZERS_BY_PROVIDER[paymentMethodConfiguration?.method_configuration?.payment_provider] ??
  keepReferenceAsIs;

export const getReferenceInitialValue = (
  claim,
  payableType,
  prefillIndemnityReferenceWithClaimNumber,
  sanitizeSuggestedReference = keepReferenceAsIs
) => {
  if ((prefillIndemnityReferenceWithClaimNumber && payableType === 'indemnity') || isQoverClaim(claim)) {
    return sanitizeSuggestedReference(claim.claim_id_display);
  }

  return '';
};

export const shouldUseInvoiceForPaymentReferenceInitialValue = (
  payableType,
  prefillExpensesReferenceWithInvoiceNumber
) => {
  return prefillExpensesReferenceWithInvoiceNumber && payableType === 'expenses';
};

// The other half of `shouldUseInvoiceForPaymentReferenceInitialValue`, applied on submit. The
// Reference input is hidden while this is on, and Yup skips reference validation entirely, so an
// invoice number the provider rejects would be an unrecoverable dead end — only the derived
// reference is sanitized, never `invoice_number` itself, which is a real document identifier that
// legitimately contains hyphens.
export const setReferenceFromInvoiceNumber = ({
  values,
  payableType,
  sanitizeSuggestedReference = keepReferenceAsIs,
}) => {
  if (payableType === 'expenses' && getIn(values, 'use_invoice_for_payment_reference')) {
    values.reference = sanitizeSuggestedReference(values.invoice_number);
  }
};

// MGM keys its indemnity payment types by claim type and coverage, every other organization by type alone.
export const getIndemnityPaymentTypesForClaim = (indemnityPaymentTypes, claim, exposure) =>
  isMgmClaim(claim) ? indemnityPaymentTypes?.[claim.type]?.[exposure.coverage_type] : indemnityPaymentTypes;

export const shouldDisablePayment = ({
  shouldDisablePaymentExceedingReserve,
  issuesAndSeveritiesForPaymentsAboveReserves,
  isSubmitting,
  checkIsExceedsClosedExposurePaymentLimit,
  isLoadingPaymentIssues,
  isPaymentIssuePreventingPayment,
  isLoadingExchangeRates,
}) => {
  return (
    (shouldDisablePaymentExceedingReserve && issuesAndSeveritiesForPaymentsAboveReserves.length > 0) ||
    isSubmitting ||
    checkIsExceedsClosedExposurePaymentLimit() ||
    isLoadingPaymentIssues ||
    isPaymentIssuePreventingPayment ||
    isLoadingExchangeRates
  );
};

export const getAnomalousPaymentBannerProps = ({
  checkIsExceedsUserAuthority,
  checkIsExceedsClosedExposurePaymentLimit,
  issuesAndSeveritiesForPaymentsAboveReserves,
  issuesAndSeveritiesForTrustedPayee = [],
  policyLimitBreaches,
  isPaymentIssuePreventingPayment,
}) => {
  const issuesAndSeverities = [];
  if (checkIsExceedsUserAuthority()) {
    issuesAndSeverities.push({
      issue: 'Payment exceeds authority level.',
      severity: AnomalousPaymentBanner.IssueSeverity.WARNING,
    });
  }
  if (checkIsExceedsClosedExposurePaymentLimit()) {
    issuesAndSeverities.push({
      issue: 'Payment exceeds limit for the closed exposure payment',
      severity: AnomalousPaymentBanner.IssueSeverity.ERROR,
    });
  }

  issuesAndSeverities.push(...issuesAndSeveritiesForPaymentsAboveReserves);
  issuesAndSeverities.push(...issuesAndSeveritiesForTrustedPayee);

  if (policyLimitBreaches) {
    policyLimitBreaches.forEach((plb) => {
      issuesAndSeverities.push({
        issue: plb.reason,
        severity: isPaymentIssuePreventingPayment
          ? AnomalousPaymentBanner.IssueSeverity.ERROR
          : AnomalousPaymentBanner.IssueSeverity.WARNING,
      });
    });
  }

  return {
    issuesAndSeverities,
  };
};
